import React, { Dispatch, Fragment, SetStateAction, forwardRef, useImperativeHandle, useRef } from 'react';

import { IComponent } from '../../../typings/generic-component';
import { makeClassNames } from '../../../utils/styles';
import Cta from '../../atoms/cta/cta';
import { CompetitionBoardCard } from '../../molecules';
import { ICompetitionMatches } from '../../templates/competition-board/competition-board';
import IconArrowLink from '../../atoms/icons/icon-arrow-link';
import { ICustomTrackingData } from '../../../typings/competition-board';
import './competition-board-match-group.scss';

interface ICompetitionBoardMatchGroupClasses {
  competitionBoardMatchGroup: string;
  competitionBoardMatchPreview: string;
  competitionBoardMatchRightLine: string;
  cta: string;
  ctaWrapper: string;
}

export interface ICompetitionBoardMatchGroup extends IComponent {
  matches: ICompetitionMatches[];
  setVisibleColumn?: Dispatch<SetStateAction<number>>;
  columnIndex: number;
  trackingData?: ICustomTrackingData;
  hasLinkedMatch?: boolean;
  cta?: { url: string; label: string };
}

const classNames = (
  hasLinkedMatch: ICompetitionBoardMatchGroup['hasLinkedMatch'] = false
): ICompetitionBoardMatchGroupClasses =>
  makeClassNames({
    competitionBoardMatchGroup: [
      'competition-board-match-group',
      hasLinkedMatch && 'competition-board-match-group--has-linked',
    ]
      .filter(Boolean)
      .join(' '),
    competitionBoardMatchPreview: 'competition-board-match-preview',
    competitionBoardMatchRightLine: 'competition-board-match-right-line',
    cta: ['competition-board-match-group__cta'],
    ctaWrapper: ['competition-board-match-group__cta-wrapper'],
  }) as ICompetitionBoardMatchGroupClasses;

const CompetitionBoardMatchGroup = forwardRef((props: ICompetitionBoardMatchGroup, ref) => {
  const { matches, columnIndex, trackingData, hasLinkedMatch = false, cta } = props;
  const [ctaIsVisible, setCtaIsVisible] = React.useState<boolean>(true);
  const ctaWrapperRef = useRef<HTMLDivElement>(null);

  const cn = classNames(hasLinkedMatch);

  useImperativeHandle(ref, () => {
    return {
      ctaWrapper: ctaWrapperRef?.current,
      columnIndex,
      setCtaIsVisible: (isVisible: boolean) => {
        setCtaIsVisible(isVisible);
      },
    };
  });

  return (
    <Fragment>
      {matches.map((match, index) => {
        if (index % 2 === 0) {
          const nextMatch = matches[index + 1];

          if (!nextMatch) {
            return (
              <CompetitionBoardCard
                needConnector={columnIndex !== 0}
                key={index}
                {...match}
                trackingData={trackingData}
              />
            );
          }

          return (
            <div key={index} className={cn.competitionBoardMatchGroup} data-competition-board-match-group>
              <CompetitionBoardCard needConnector={columnIndex !== 0} {...match} trackingData={trackingData} />
              {nextMatch && (
                <CompetitionBoardCard needConnector={columnIndex !== 0} {...nextMatch} trackingData={trackingData} />
              )}

              <div className={cn.competitionBoardMatchRightLine} />
            </div>
          );
        }
        return null;
      })}
      {cta ? (
        <div ref={ctaWrapperRef} className={cn.ctaWrapper}>
          {ctaIsVisible ? (
            <Cta
              color="light"
              className={cn.cta}
              href={cta.url}
              trackingData={{ intcmp: trackingData?.calendarResult }}
            >
              {cta.label}
              <IconArrowLink color="light" size="xxsmall" />
            </Cta>
          ) : null}
        </div>
      ) : null}
    </Fragment>
  );
});

CompetitionBoardMatchGroup.displayName = 'CompetitionBoardMatchGroup';

export default CompetitionBoardMatchGroup;
